import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner'

import { CheckCircleIcon, ClockIcon, TrashIcon } from "@heroicons/react/24/solid";
import { Progress, Tooltip } from "@material-tailwind/react";
import { TbLoader3 } from "react-icons/tb";
import { ProductCreationModal } from "./ProdCreationModal";
import MultDownloadBtn from './MultDownloadBtn';
import useProdCreationStore from '../../../../utils/store/warehouse';
import DataTableShell from '../../../../Components/table/DataTableShell';
import TableToolbar from '../../../../Components/table/TableToolbar';
import { TableEmptyRow, TableIdCell, TableSkeletonRows } from '../../../../Components/table/TableCells';

const TABLE_HEAD = ["Batch ID", "Uploaded by", "Brand", "Sub Brand", "Status", "Actions"];

export function ProdCreationTable() {

  const [loading, setLoading] = useState(true)
  const [currentPage, setCurrentPage] = useState(1);
  const [isAltered, setIsAltered] = useState(false);

  const { products, fetchProdCreation, total } = useProdCreationStore();

  const itemsPerPage = 10; 
  const sortedProducts = useMemo(
    () => [...products].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)),
    [products]
  );
  const totalPages = Math.max(1, Math.ceil(total / itemsPerPage));

  useEffect(() => {
    setLoading(true)
    fetchProdCreation(currentPage, itemsPerPage).finally(() => setLoading(false))
  }, [fetchProdCreation, currentPage, isAltered])

  const handleAlterState = () => {
    setIsAltered(prevstate => !prevstate);
  };

  const handleDeleteBatch =(batchId) => {
    const isConfirmed = window.confirm(`Are you sure you want to delete this batch: ${batchId}?`)

    if (isConfirmed) {
      axios.delete(`/api/extensive-prod-creation-requests/delete/${batchId}`)
        .then(response => {
            if (response.data.message) {
                toast.success(`${response.data.message}`)
                setIsAltered(prevState => !prevState)
                axios.delete(`/api/extensive-prod-creation/delete/${batchId}`)
                .then(response => {console.log(response.data.result)})
            } else {
                toast.error(`${response.data.message}`)
                console.log('Error:', response.data.error)
            }
        })
        .catch(error => {
            console.error('Error:', error)
        }) 
    }
  }

  return (
    <DataTableShell
      minWidth="980px"
      currentPage={currentPage}
      setCurrentPage={setCurrentPage}
      totalPages={totalPages}
      toolbar={
        <TableToolbar
          kicker="Extensiv"
          title="Product creation batches"
          subtitle={`${total} total batches · server page ${currentPage}`}
          actions={<ProductCreationModal onAlterState={handleAlterState}/>}
        />
      }
    >
          <thead>
            <tr>
              {TABLE_HEAD.map((head) => (
                <th key={head}>{head}</th>
              ))}
            </tr>
          </thead>
          <tbody>
          {loading ?
            <TableSkeletonRows columns={TABLE_HEAD.length} rows={9} />
            : sortedProducts.length === 0 ?
            <TableEmptyRow columns={TABLE_HEAD.length} title="No product batches found" description="Import a product file to create a batch." />
            :
            (sortedProducts.map((item) => {
              const date = new Date(item.createdAt);
              const formattedTime = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true });
              const options = { month: 'long', day: 'numeric', year: 'numeric' };
              const formattedDate = date.toLocaleDateString('en-US', options);
              const formattedDateTime = `${formattedDate} ${formattedTime}`;

              const brands = item.importData.map(product => product.brand);
              const subBrands = item.importData.map(product => product.subBrand);
            
              const getDisplayAndRemaining = (arr) => {
                const uniqueItems = [...new Set(arr.filter(item => item))]; 
                return {
                  display: uniqueItems.slice(0, 2),
                  remaining: uniqueItems.slice(2),
                };
              };
              // Get display and remaining values for each field
              const { display: displayBrands, remaining: remainingBrands } = getDisplayAndRemaining(brands);
              const { display: displaySubBrands, remaining: remainingSubBrands } = getDisplayAndRemaining(subBrands);

              return (
                <tr key={item.batchId}>
                  <td>
                    <div className="flex items-center gap-1">
                      <TableIdCell>{item.batchId}</TableIdCell>
                      <Tooltip content={formattedDateTime}>
                        <ClockIcon className='h-4 w-4 text-muted' />
                      </Tooltip>
                    </div>
                  </td>
                  <td>{item.addedBy}</td>

                  {/* Brands */}                
                  <td>
                    <div className="flex flex-wrap items-center gap-1">
                      {displayBrands.map((brand, index) => (
                        <span className='nx-chip-info' key={`${brand}-${index}`}>{brand}</span>
                      ))}
                      {remainingBrands.length > 0 && (
                        <Tooltip content={remainingBrands.map((brand, index) => <div key={index}>{brand}</div>)}>
                          <span className='cursor-help text-xs text-muted'>{`+ ${remainingBrands.length} more`}</span>
                        </Tooltip>
                      )}
                    </div>
                  </td>

                  {/* SubBrands */}
                  <td>
                    <div className="flex flex-wrap items-center gap-1">
                      {displaySubBrands.map((subBrand, index) => (
                        <span className='nx-chip-idle' key={`${subBrand}-${index}`}>{subBrand}</span>
                      ))}
                      {remainingSubBrands.length > 0 && (
                        <Tooltip content={remainingSubBrands.map((subBrand, index) => <div key={index}>{subBrand}</div>)}>
                          <span className='cursor-help text-xs text-muted'>{`+ ${remainingSubBrands.length} more`}</span>
                        </Tooltip>
                      )}
                    </div>
                  </td>

                  <td>
                    <div className="mb-2 flex items-center justify-between gap-4 text-xs">
                      {item.progress === 100 ? 
                        <div className='flex items-center text-success'>Completed<CheckCircleIcon className='ml-1 h-4 w-4'/></div> 
                        : 
                        <div className='flex items-center animate-pulse text-warning'>In progress<TbLoader3 className='ml-1 h-4 w-4 animate-spin'/></div>
                      }
                      <span className="font-mono text-paper">{item.progress}%</span>
                    </div>
                    <Progress value={item.progress} color= {item.progress === 100 ? 'green' : 'orange'}/>
                  </td>

                  <td>
                    <div className="flex items-center gap-2">
                      {item.progress === 100 ?
                        <>

                          <MultDownloadBtn batchId={item.batchId}/> 
                          <button type="button" className="nx-btn-ghost p-2 text-danger" onClick={() => handleDeleteBatch(item.batchId)} title="Delete batch"> 
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </>
                          :
                          <button type="button" className="nx-btn-ghost p-2 text-danger" onClick={() => handleDeleteBatch(item.batchId)} title="Delete batch"> 
                            <TrashIcon className="h-4 w-4" />
                          </button>
                      }
                    </div>
                  </td>
                </tr>
              );
            }))
          }
          </tbody>
    </DataTableShell>

  );
}